'use client';

import React, { useEffect, useState } from 'react';
import { authApi, publicApi } from '@/lib/api';

/**
 * Botao "Continuar com Google" - dispara o fluxo OAuth via hard-redirect
 * pra GET /api/v1/auth/google. Backend cuida do consent + callback +
 * decisao de linkar conta existente vs finalizar cadastro novo.
 *
 * Verifica /platform/oauth-status antes de renderizar - se Google nao
 * tiver credencial cadastrada no SystemSecretVault, o botao some
 * (feature flag implicita). Doc: docs/06-integrations/auth-google-oauth.md
 *
 * NOTA: usa fetch direto (useState + useEffect) em vez de useQuery do
 * TanStack porque as paginas /login e /register NAO estao envolvidas
 * por QueryClientProvider (sao paginas leves de auth). useQuery aqui
 * causaria "No QueryClient set" durante o SSR e quebraria o boot do
 * container web (healthcheck do Swarm dispara rollback).
 *
 * Logo + cores oficiais do Google brand guidelines (light theme):
 *   https://developers.google.com/identity/branding-guidelines
 */
interface GoogleButtonProps {
  /** Texto do botao - default "Continuar com Google". Use "Entrar com Google" no /login. */
  label?: string;
  /**
   * Estado ja resolvido pelo pai (via useGoogleOAuthEnabled) - evita fetch
   * duplicado quando a pagina tambem condiciona elementos vizinhos (ex.:
   * divider "OU...") a disponibilidade do Google.
   */
  enabled?: boolean;
}

/**
 * Consulta /platform/oauth-status uma vez e devolve se o login Google esta
 * disponivel (credencial cadastrada no SystemSecretVault). `null` enquanto
 * carrega. fetch direto (sem useQuery) - paginas pre-login nao tem
 * QueryClientProvider.
 */
export function useGoogleOAuthEnabled(skip = false): boolean | null {
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    if (skip) return;
    let cancelled = false;
    publicApi
      .getOAuthStatus()
      .then((res) => {
        if (!cancelled) setEnabled(Boolean(res.data?.google));
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [skip]);

  return enabled;
}

export function GoogleButton({
  label = 'Continuar com Google',
  enabled: enabledProp,
}: GoogleButtonProps) {
  const enabledFetched = useGoogleOAuthEnabled(enabledProp !== undefined);
  const enabled = enabledProp ?? enabledFetched;

  if (enabled !== true) return null;

  return (
    <button
      type="button"
      className="btn btn--auth-google btn--lg btn--block google-button"
      onClick={() => authApi.googleAuthStart()}
      aria-label={label}
    >
      <GoogleLogo />
      <span>{label}</span>
    </button>
  );
}

function GoogleLogo() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      width="20"
      height="20"
      aria-hidden="true"
    >
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}
